import { storeToRefs } from 'pinia';
import { computed, ref, toValue, watch } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import { useRoute } from 'vue-router';

import type { PartnerListSummary } from '@stuut/api-sdk/models';
import type { Partner } from '@stuut/api/models';
import { useLoader } from '@ui/components/combo-select/useLoader';

import { usePartners } from '@/store';

/** Raw union returned by the store — used only for ComboSelect v-model compat. */
export type SelectPartner = PartnerListSummary | Partner;

/**
 * Normalized partner shape used by all emit/consumer code.
 * Both SDK flavors (Speakeasy camelCase and legacy snake_case) map to this.
 */
export interface PartnerRef {
  accountNumber: string | null;
  id: string;
  name: string | null;
}

/**
 * Central normalizer — converts either SDK flavour to a stable PartnerRef.
 * Use at every emit/consumer boundary so downstream code never has to
 * deal with the camelCase vs snake_case duality.
 */
export function toPartnerRef(p: SelectPartner): PartnerRef {
  // Legacy Partner class (snake_case) — detected by the snake_case field
  if ('account_number' in p) {
    return { id: p.id, name: p.name ?? null, accountNumber: p.account_number };
  }
  // Speakeasy PartnerListSummary (camelCase plain object)
  return { id: p.id, name: p.name ?? null, accountNumber: p.accountNumber };
}

interface UsePartnerSelectOptions {
  partnerId?: MaybeRefOrGetter<string | null | undefined>;
  queryName?: string;
}

export function usePartnerSelect(options: UsePartnerSelectOptions = {}) {
  const partnerStore = usePartners();
  const { partnerSelect: partners } = storeToRefs(partnerStore);
  const route = useRoute();

  const selectedPartner = ref<SelectPartner | null>(null);
  const isLoading = ref(false);

  const effectivePartnerId = computed<string | null | undefined>(() => {
    const id = toValue(options.partnerId);
    if (id != null && id !== '') return id;
    if (options.queryName) {
      const queryValue = route.query?.[options.queryName];
      return (Array.isArray(queryValue) ? queryValue[0] : queryValue) ?? undefined;
    }
    return undefined;
  });

  const partnerLoader = useLoader({
    load: async (args?) => {
      if (args?.search !== undefined) {
        await partnerStore.fetchPartnerSelect(args);
      } else if (!partnerStore.selectHasFullList) {
        await partnerStore.fetchPartnerSelect({});
      }
      return partners.value;
    },
  });

  const syncFromId = async (id: string | null | undefined) => {
    if (!id) {
      selectedPartner.value = null;
      return;
    }
    isLoading.value = true;
    try {
      selectedPartner.value = await partnerStore.getPeekFindSelect(id);
    } finally {
      isLoading.value = false;
    }
  };

  watch(effectivePartnerId, (id) => void syncFromId(id), { immediate: true });

  return { selectedPartner, isLoading, partnerLoader };
}
